import type { LegalDocumentId } from '@tipi/shared'

const termsMarkdown = `# Términos y Condiciones de Uso — Tipi

**Última actualización:** 11 de julio de 2026

## 1. Identificación del titular

El presente sitio web y la aplicación Tipi (en adelante, la «Aplicación» o el «Servicio») son titularidad de:

- **Titular:** INNOVATION ROOT, S.L. (en adelante, «Tipi», «Titular» o «nosotros»)
- **NIF:** B19933365
- **Domicilio social:** Calle La Luz, núm. 63, planta 0, Santo Ángel, 30151 Murcia, España
- **Correo electrónico de contacto:** management@oktipi.com
- **Datos registrales:** Sociedad constituida ante el Notario de Murcia D. Escolano Navarro el 18 de julio de 2024, bajo el número 2.748 de su protocolo, e inscrita en el Registro Mercantil de Murcia, hoja MU-113952, inscripción 1ª.

## 2. Objeto y aceptación

2.1. Los presentes Términos y Condiciones (en adelante, «Condiciones») regulan el acceso y uso del Servicio, así como cualquier contrato o relación jurídica conexa que se establezca con el Titular.

2.2. El Servicio se dirige tanto a empresas y profesionales (autónomos) que actúan en el marco de su actividad («Usuarios Profesionales») como, en su caso, a personas físicas que actúan con un propósito ajeno a su actividad empresarial o profesional («Consumidores»). Determinadas cláusulas se aplican de forma diferenciada según la categoría del Usuario; tales diferencias se indican expresamente.

2.3. El uso del Servicio requiere la aceptación previa, expresa e inequívoca de las presentes Condiciones y de la [Política de Privacidad](/privacy-policy), mediante la marcación de las casillas correspondientes en el proceso de registro. Dicha aceptación se registra de forma auditable (identificador de usuario, versión del documento aceptado y fecha/hora). Toda persona que acceda al Servicio —tanto el contratante como cualquier usuario invitado por este— debe aceptar las presentes Condiciones en su primer acceso.

2.4. Si el Usuario no está de acuerdo con las Condiciones, debe abstenerse de utilizar el Servicio.

## 3. Descripción del Servicio

3.1. Tipi es una plataforma de software como servicio (SaaS) dirigida a la gestión de costes en el sector de la hostelería. El Servicio incluye, entre otras, las siguientes funcionalidades:

- **Gestión y digitalización de documentos:** captura y lectura automatizada (OCR) de albaranes y facturas de proveedores para el seguimiento de precios y costes de aprovisionamiento.
- **Asistente de voz:** funcionalidad que permite al Usuario dictar recetas y datos de escandallo mediante grabaciones de voz, que son transcritas y procesadas para el cálculo de costes.
- **Cálculo de escandallos** y control de coste de materia prima.

3.2. El Servicio se presta «según su estado y disponibilidad» en cada momento. El Titular podrá añadir, modificar o suprimir funcionalidades, informando adecuadamente a los Usuarios cuando el cambio sea sustancial.

3.3. Tipi es una herramienta de apoyo a la gestión y no constituye asesoramiento fiscal, contable ni financiero. El Usuario es el único responsable de las decisiones que adopte con base en la información proporcionada por el Servicio, y de conservar los originales de sus documentos conforme a sus propias obligaciones legales (véase cláusula 12).

## 4. Registro y cuenta de usuario

4.1. Para utilizar el Servicio es necesario registrarse facilitando datos veraces, completos y actualizados. El registro puede realizarse mediante autenticación con Google u otros métodos habilitados. En todos los métodos de registro es obligatoria la aceptación de estas Condiciones y de la [Política de Privacidad](/privacy-policy).

4.2. El Usuario es responsable de la custodia y confidencialidad de sus credenciales y de toda actividad realizada a través de su cuenta. Debe notificar de inmediato al Titular cualquier uso no autorizado.

4.3. **Usuarios invitados:** el Usuario contratante puede invitar a terceros (empleados o colaboradores) a acceder a su cuenta. El contratante es responsable de la gestión de dichos accesos. Cada usuario invitado deberá aceptar las presentes Condiciones y la [Política de Privacidad](/privacy-policy) en su primer acceso.

4.4. El Titular podrá suspender o cancelar cuentas que incumplan estas Condiciones, previa comunicación al Usuario salvo casos de urgencia o requerimiento legal.

## 5. Planes, precios y facturación

5.1. **Precios.** El Servicio se ofrece bajo suscripción mensual. La cuota base es de 100 € al mes (IVA incluido) e incluye el procesamiento de hasta 40 albaranes mensuales. Por cada tramo adicional de 40 albaranes mensuales, la cuota se incrementa en 50 € al mes (IVA incluido). Cuando el uso del Usuario vaya a superar el umbral de su tramo y generar un incremento de cuota, el Titular lo advertirá de forma clara antes de procesar el documento que origine el sobrecoste, de modo que el Usuario conozca la nueva cuota antes de que se aplique.

5.2. Todos los precios se expresan en euros (€) e incluyen los impuestos indirectos aplicables, salvo que se indique lo contrario.

5.3. **Renovación automática.** La suscripción se renueva automáticamente por periodos mensuales, cargándose la cuota en el método de pago del Usuario, hasta que este cancele conforme a la cláusula 7.

5.4. **Modificación de precios.** El Titular podrá modificar los precios, comunicándolo al Usuario con una antelación mínima de 30 días naturales. Las nuevas tarifas se aplicarán a partir del siguiente periodo de renovación. Si el Usuario no está conforme, podrá cancelar sin penalización antes de que surtan efecto.

## 6. Prueba gratuita y pago

6.1. **Prueba gratuita.** El Titular ofrece un periodo de prueba gratuito de 7 días a los nuevos Usuarios. Al finalizar dicho periodo, y salvo cancelación previa, se cargará automáticamente la cuota correspondiente al plan seleccionado.

6.2. **Procesamiento de pagos.** Los pagos se procesan a través de Stripe (Stripe Payments Europe, Ltd.). El Titular no almacena los datos completos de la tarjeta del Usuario. El uso del sistema de pago se rige adicionalmente por los términos de Stripe.

6.3. El Usuario se compromete a mantener actualizados sus datos de pago. La imposibilidad de cobro podrá dar lugar a la suspensión del Servicio.

## 7. Cancelación

7.1. El Usuario puede cancelar su suscripción en cualquier momento y de forma autónoma a través del portal de la plataforma de pagos (Stripe), o bien contactando con el Titular en la dirección de contacto indicada.

7.2. La cancelación surtirá efecto al final del periodo de facturación en curso ya abonado. El Usuario mantendrá el acceso al Servicio hasta esa fecha. No se generan cuotas posteriores.

## 8. Reembolsos

8.1. Con carácter general, las cuotas abonadas no son reembolsables una vez iniciado el periodo de facturación.

8.2. **Excepción:** el Titular reembolsará la cuota del mes en curso exclusivamente en caso de problemas técnicos imputables al Servicio que impidan su uso normal. La falta de uso, el desuso de la Aplicación o circunstancias análogas no dan derecho a reembolso.

8.3. Lo anterior se entiende sin perjuicio de los derechos que la normativa de consumo reconozca a los Usuarios Consumidores (véase cláusula 9).

## 9. Derecho de desistimiento (solo Usuarios Consumidores)

9.1. Si el Usuario es Consumidor, dispone en principio de un plazo de 14 días naturales para desistir del contrato sin necesidad de justificación, conforme al Real Decreto Legislativo 1/2007 (TRLGDCU).

9.2. **Prestación inmediata y renuncia.** Dado que el Servicio consiste en la prestación de contenidos/servicios digitales de ejecución inmediata, si el Consumidor solicita que la prestación comience durante el plazo de desistimiento (p. ej., al iniciar el uso durante la prueba o tras el alta), reconoce y acepta expresamente que perderá su derecho de desistimiento una vez que el servicio haya sido completamente ejecutado, conforme al art. 103.m) TRLGDCU. Para que esta renuncia sea válida, se recabará mediante una casilla separada y específica en el proceso de alta (distinta de la aceptación general de estas Condiciones), con información expresa de que el Consumidor pierde su derecho de desistimiento.

9.3. Para ejercer el desistimiento, el Consumidor puede comunicarlo por cualquier medio inequívoco a la dirección de contacto.

## 10. Propiedad intelectual

10.1. Todos los derechos de propiedad intelectual e industrial sobre el Servicio, su software, diseño, marcas y contenidos son titularidad de Tipi o de sus licenciantes. Se concede al Usuario una licencia limitada, no exclusiva, revocable e intransferible para usar el Servicio conforme a estas Condiciones.

10.2. **Contenido del Usuario.** El Usuario conserva la titularidad de los datos y contenidos que introduce en el Servicio (albaranes, recetas, grabaciones de voz, etc.). El Usuario concede a Tipi únicamente la licencia estrictamente necesaria para prestar el Servicio —esto es, para alojar, procesar, transcribir y analizar dichos contenidos con la finalidad de ofrecer las funcionalidades contratadas—, así como, en su caso, para el mantenimiento y la resolución de incidencias técnicas. Esta licencia no es perpetua ni irrevocable, se limita a las finalidades descritas y finaliza con la supresión del contenido o la terminación del contrato, salvo obligación legal de conservación. Tipi no utilizará el contenido del Usuario con fines publicitarios ni lo cederá a terceros fuera de lo previsto en la [Política de Privacidad](/privacy-policy).

## 11. Usos aceptables

11.1. El Usuario se compromete a usar el Servicio conforme a la ley y a estas Condiciones, y a no: (a) usarlo con fines ilícitos; (b) vulnerar derechos de terceros; (c) introducir datos de terceros sin base legítima; (d) intentar acceder, alterar o dañar los sistemas del Servicio; (e) realizar ingeniería inversa salvo en la medida permitida por ley; (f) usar medios automatizados no autorizados.

11.2. El Usuario es responsable de la licitud de los datos que introduce, incluidos los datos personales de terceros contenidos en los documentos que sube (véase cláusula 12 y el [Contrato de Encargo de Tratamiento](/data-processing-agreement)).

## 12. Datos de terceros y obligaciones de conservación del Usuario

12.1. Los documentos que el Usuario sube al Servicio (albaranes, facturas) pueden contener datos personales de terceros (p. ej., datos de contacto de proveedores). Respecto de esos datos, el Usuario actúa como responsable del tratamiento y Tipi como encargado, rigiéndose la relación por el [Contrato de Encargo de Tratamiento](/data-processing-agreement) conforme al art. 28 RGPD.

12.2. Tipi no es un archivo legal ni fiscal del Usuario. El Usuario es el único responsable de conservar los originales de sus facturas y albaranes durante los plazos exigidos por su normativa fiscal y mercantil aplicable. Tipi conserva los documentos subidos durante el periodo indicado en la [Política de Privacidad](/privacy-policy) (actualmente, hasta 12 meses desde la cancelación de la cuenta) y podrá suprimirlos transcurrido dicho plazo, sin que ello genere responsabilidad alguna para Tipi.

## 13. Disponibilidad e interrupciones

13.1. El Titular procurará mantener la disponibilidad del Servicio, pero no garantiza su funcionamiento ininterrumpido. Podrá suspenderlo temporalmente por mantenimiento, actualizaciones o causas técnicas, informando cuando sea posible.

13.2. No existe un compromiso de nivel de servicio (SLA) específico salvo que se pacte expresamente por escrito.

## 14. Responsabilidad

14.1. **Frente a Usuarios Profesionales:** en la máxima medida permitida por la ley, la responsabilidad total del Titular por cualquier reclamación derivada del Servicio se limitará al importe abonado por el Usuario en los 12 meses anteriores al hecho causante. Se excluyen los daños indirectos, lucro cesante y pérdida de datos, salvo dolo o negligencia grave.

14.2. **Frente a Usuarios Consumidores:** nada en estas Condiciones limita o excluye la responsabilidad del Titular en la medida en que ello no esté permitido por la normativa imperativa de protección de los consumidores. Las limitaciones anteriores no se aplicarán cuando resulten abusivas conforme al TRLGDCU.

14.3. El Titular no se responsabiliza de la exactitud de los datos introducidos por el Usuario ni de las decisiones que este adopte con base en el Servicio.

## 15. Protección de datos

El tratamiento de datos personales se rige por la [Política de Privacidad](/privacy-policy) y, respecto de los datos de terceros contenidos en los documentos del Usuario, por el [Contrato de Encargo de Tratamiento](/data-processing-agreement), ambos disponibles en la Aplicación e incorporados por referencia a estas Condiciones.

## 16. Duración y terminación

16.1. El contrato tiene duración indefinida mientras el Usuario mantenga una suscripción activa.

16.2. Cualquiera de las partes podrá resolver el contrato conforme a estas Condiciones. El Titular podrá resolverlo por incumplimiento del Usuario. Tras la terminación, se aplicará la política de conservación y supresión de datos prevista en la [Política de Privacidad](/privacy-policy).

## 17. Modificaciones de las Condiciones

17.1. El Titular podrá modificar estas Condiciones. Los cambios sustanciales se comunicarán al Usuario y, cuando proceda, se requerirá una nueva aceptación en el siguiente acceso a la Aplicación, registrándose de forma auditable. El uso continuado tras la comunicación implica la aceptación de la versión vigente en los términos que la ley permita.

## 18. Ley aplicable y jurisdicción

18.1. Estas Condiciones se rigen por la legislación española.

18.2. **Usuarios Profesionales:** las partes se someten a los juzgados y tribunales de Murcia, con renuncia a cualquier otro fuero que pudiera corresponderles.

18.3. **Usuarios Consumidores:** será competente el juzgado del domicilio del Consumidor, conforme a la normativa imperativa aplicable. El Consumidor dispone además de la plataforma de resolución de litigios en línea de la Comisión Europea (https://ec.europa.eu/consumers/odr).

## 19. Contacto

Para cualquier cuestión relativa a estas Condiciones:

**INNOVATION ROOT, S.L.** — Calle La Luz, núm. 63, planta 0, Santo Ángel, 30151 Murcia — management@oktipi.com

---

**Documentos relacionados**

- [Política de Privacidad](/privacy-policy)
- [Política de Cookies](/cookie-policy)
- [Contrato de Encargo de Tratamiento](/data-processing-agreement)
`

const privacyMarkdown = `# Política de Privacidad — Tipi

**Última actualización:** 14 de agosto de 2026

## 1. Responsable del tratamiento

- **Responsable:** INNOVATION ROOT, S.L. («Tipi»)
- **NIF:** B19933365
- **Domicilio:** Calle La Luz, núm. 63, planta 0, Santo Ángel, 30151 Murcia, España
- **Contacto en materia de protección de datos:** management@oktipi.com

No se ha designado Delegado de Protección de Datos (DPO), al no concurrir actualmente los supuestos del art. 37 RGPD. Este punto se revisará si la escala del tratamiento aumenta significativamente.

## 2. Ámbito y doble rol de Tipi

Esta Política describe cómo Tipi trata datos personales como responsable: datos de los propios Usuarios (cuenta, uso, voz, pago).

Adicionalmente, cuando el Usuario sube documentos (albaranes, facturas) que contienen datos personales de terceros, Tipi actúa como encargado del tratamiento por cuenta del Usuario (que es el responsable de esos datos). Esa relación se rige por el [Contrato de Encargo de Tratamiento](/data-processing-agreement) (art. 28 RGPD), documento independiente. Véase la sección 7.

## 3. Qué datos tratamos y con qué finalidad y base jurídica

| Categoría de datos | Finalidad | Base jurídica (art. 6 RGPD) |
| --- | --- | --- |
| Datos de registro y cuenta (nombre, email, contraseña o autenticación con Google, ID de usuario) | Crear y gestionar la cuenta; autenticar el acceso | Ejecución del contrato (6.1.b) |
| Datos de uso y dispositivo (logs, IP, actividad en la app) | Prestar, mantener y asegurar el Servicio; resolver incidencias | Interés legítimo (6.1.f) / Ejecución del contrato (6.1.b) |
| Contenido de documentos (albaranes y facturas, incl. posibles datos de terceros) | Digitalización (OCR), seguimiento de precios y cálculo de costes | Ejecución del contrato (6.1.b). Respecto a datos de terceros, Tipi actúa como encargado |
| Grabaciones de voz y transcripciones | Transcribir y generar escandallos; resolver incidencias técnicas mediante revisión humana | Ejecución del contrato (6.1.b) |
| Datos de pago (gestionados por Stripe; Tipi no almacena el número completo de tarjeta) | Procesar la suscripción | Ejecución del contrato (6.1.b) |
| Datos de contacto y navegación en la web (analítica, marketing) | Estadística y, en su caso, publicidad y atribución de anuncios | Consentimiento (6.1.a) vía banner de cookies o casilla del formulario de contacto |
| Datos para defensa jurídica y cumplimiento | Ejercer o defender reclamaciones; cumplir obligaciones legales | Obligación legal (6.1.c) / Interés legítimo (6.1.f) |

## 4. Funcionalidad de asistente de voz y procesamiento con IA

4.1. Tipi utiliza la API de OpenAI para tres funciones: (a) el asistente de voz (transcripción y generación de escandallos), (b) el procesamiento de recetas y (c) el análisis de llamadas.

4.2. Respecto a la funcionalidad de voz:

- **Qué recogemos:** el audio de la grabación y su transcripción.
- **Para qué:** transcribir el contenido y generar los escandallos; y resolver incidencias técnicas mediante revisión humana por nuestro servicio técnico cuando sea necesario.
- **Con quién se comparte:** el procesamiento se realiza con OpenAI (véase sección 7).

4.3. **Entrenamiento de modelos.** OpenAI no utiliza los datos enviados a través de su API para entrenar sus modelos (política por defecto de OpenAI para la API). Por tanto, las grabaciones y transcripciones no se emplean para entrenar modelos de IA.

4.4. **Retención por OpenAI.** OpenAI puede conservar los datos enviados a su API durante un periodo limitado (por defecto, hasta 30 días) con fines de monitorización de abuso, tras lo cual los elimina, salvo obligación legal.

## 5. Digitalización de documentos (OCR)

5.1. Las imágenes de albaranes y facturas se procesan mediante un proveedor de OCR externo, MATIL SOLUTIONS, S.L. (NIF B22500458), que actúa como encargado/subencargado por cuenta de Tipi, sujeto a contrato conforme al art. 28 RGPD.

5.2. Dado que estos documentos pueden contener datos de terceros, su tratamiento se enmarca en el rol de Tipi como encargado del Usuario (sección 2 y [Contrato de Encargo de Tratamiento](/data-processing-agreement)).

## 6. Fuentes de los datos

Tratamos los datos que el Usuario nos facilita directamente (registro, documentos, voz) y los que se generan automáticamente por el uso del Servicio (datos de uso, logs, identificadores técnicos).

## 7. Destinatarios y encargados de tratamiento

Compartimos datos con los siguientes proveedores, que actúan como encargados bajo contrato:

| Proveedor | Servicio | Ubicación | Garantía transferencias |
| --- | --- | --- | --- |
| Google (Firebase) | Hosting, base de datos, autenticación, mensajería, analítica de app (Firebase Analytics, G-0LEK4G5JZ4) | UE (europe-west) | Tratamiento en UE. SCC para accesos desde fuera del EEE |
| MATIL SOLUTIONS, S.L. | OCR de documentos | España (UE) | No aplica (UE) |
| OpenAI | Voz, recetas y análisis de llamadas | EE. UU. (por defecto) | SCC + DPF (certificado) |
| Stripe (Payments Europe, Ltd.) | Procesamiento de pagos | UE / EE. UU. | SCC / DPF |
| Holded (Holded Technologies, S.L.) | Facturación (facturas de Tipi a clientes) | España (UE) | No aplica (UE) |
| Mixpanel | Analítica de producto | UE (api-eu.mixpanel.com, proxy europe-southwest1) | Tratamiento en UE |
| Google Analytics 4, Meta (Pixel y atribución de anuncios), ChatGPT Ads Measurement Pixel | Analítica y marketing web | EE. UU. | Consentimiento (banner y/o casilla del formulario) + SCC/DPF |

También podremos comunicar datos a autoridades y organismos públicos cuando exista obligación legal, y en el contexto de operaciones societarias, exigiendo confidencialidad.

## 8. Transferencias internacionales

Algunos proveedores (OpenAI, Stripe, herramientas de analítica) pueden tratar datos fuera del Espacio Económico Europeo, principalmente en EE. UU. En tales casos, la transferencia se ampara en garantías adecuadas: cláusulas contractuales tipo aprobadas por la Comisión Europea y/o la adhesión del proveedor al EU-US Data Privacy Framework. El Usuario puede solicitar información sobre dichas garantías en la dirección de contacto.

## 9. Plazos de conservación

9.1. **Datos de cuenta, uso, documentos y voz:** se conservan mientras la cuenta esté activa y, tras la cancelación, hasta un máximo de 12 meses, salvo obligación legal de conservar por más tiempo o base legítima que lo justifique.

9.2. **Facturas emitidas por Tipi al Usuario:** se conservan durante los plazos exigidos por la normativa mercantil y fiscal española (con carácter general, hasta 6 años conforme al Código de Comercio y 4 años a efectos tributarios). Estas facturas se gestionan y almacenan en Holded y en el almacenamiento en la nube de Gmail/Google Workspace, no en la infraestructura del Servicio. Es independiente de la política general de 12 meses y afecta solo a las facturas que Tipi emite, no a los documentos que el cliente sube.

9.3. **Evidencia de consentimiento y aceptación de términos:** se conservan, por interés legítimo de defensa jurídica, durante 5 años desde el fin de la relación contractual (prescripción de acciones personales, art. 1964 CC). Es una excepción declarada a la política general de 12 meses.

9.4. Transcurridos los plazos, los datos se suprimen o anonimizan de forma segura.

## 10. Derechos de los usuarios

El Usuario puede ejercer los siguientes derechos dirigiéndose a management@oktipi.com:

- Acceso, rectificación y supresión de sus datos.
- Limitación y oposición al tratamiento.
- Portabilidad de los datos.
- Retirada del consentimiento en cualquier momento (sin efecto retroactivo).
- No ser objeto de decisiones automatizadas con efectos jurídicos significativos.

Responderemos en el plazo máximo de un mes. Si el Usuario considera que no hemos atendido correctamente sus derechos, puede reclamar ante la Agencia Española de Protección de Datos (AEPD) — www.aepd.es.

## 11. Seguridad

Aplicamos medidas técnicas y organizativas apropiadas para proteger los datos (control de accesos, cifrado en tránsito, alojamiento en infraestructura Firebase con configuración en la UE). Ningún sistema es infalible, pero trabajamos para mantener un nivel de seguridad adecuado al riesgo.

## 12. Menores

El Servicio se dirige a mayores de 18 años. No recabamos conscientemente datos de menores. Si detectamos datos de un menor, procederemos a su supresión.

## 13. Cambios en esta Política

Podremos actualizar esta Política. Comunicaremos los cambios sustanciales y, cuando el tratamiento se base en el consentimiento, recabaremos uno nuevo si fuera necesario. La fecha de última actualización figura al inicio.

---

**Documentos relacionados**

- [Términos y Condiciones](/terms-and-conditions)
- [Política de Cookies](/cookie-policy)
- [Contrato de Encargo de Tratamiento](/data-processing-agreement)
`

const cookiePolicyMarkdown = `# Política de Cookies y Rastreadores — Tipi

**Última actualización:** 14 de agosto de 2026

## 1. Introducción

Esta Política informa sobre las cookies y tecnologías de seguimiento («**Rastreadores**») que utiliza Tipi, con qué finalidad, y cómo el Usuario puede gestionarlas.

Un Rastreador es cualquier tecnología (cookies, identificadores, píxeles, scripts incrustados, huella digital) que permite acceder a información o almacenarla en el dispositivo del Usuario.

## 2. Responsable

**INNOVATION ROOT, S.L.** — NIF B19933365 — Calle La Luz, núm. 63, planta 0, Santo Ángel, 30151 Murcia, España — management@oktipi.com

## 3. Categorías de rastreadores

### 3.1. Necesarios (no requieren consentimiento)

Imprescindibles para el funcionamiento y la seguridad del Servicio.

| Rastreador | Proveedor | Finalidad |
| --- | --- | --- |
| Firebase Authentication / sesión | Google (UE) | Autenticación y mantenimiento de sesión |
| Firebase Performance Monitoring | Google (UE) | Estabilidad y rendimiento |

### 3.2. Medición / analítica (requieren consentimiento)

| Rastreador | Proveedor | Ubicación | Duración |
| --- | --- | --- | --- |
| Google Analytics 4 (\`_ga\`, \`_ga_*\`) | Google LLC | EE. UU. | 2 años |
| Mixpanel | Mixpanel, Inc. | Unión Europea (\`api-eu.mixpanel.com\`, proxy \`europe-southwest1\`) | Según configuración del proyecto |
| Firebase Analytics (app, propiedad \`G-0LEK4G5JZ4\`) | Google | UE | Según configuración de Firebase |

### 3.3. Marketing / publicidad (requieren consentimiento)

| Rastreador | Proveedor | Ubicación | Duración |
| --- | --- | --- | --- |
| Meta Pixel (\`_fbp\`, \`_fbc\`, \`fr\`) | Meta Platforms, Inc. | EE. UU. | 3 meses |
| Google Ads | Google LLC | EE. UU. | Según configuración de Google |
| ChatGPT Ads Measurement Pixel (\`__oppref\`) | OpenAI OpCo, LLC | EE. UU. | Según configuración de OpenAI |
| Google Tag Manager (contenedor de los anteriores) | Google LLC | EE. UU. | — |

## 4. Gestión del consentimiento

4.1. Al acceder a la web (\`oidotipi.com\`) o a la app (\`app.oidotipi.com\`), se muestra un banner que permite **aceptar, rechazar o configurar** los Rastreadores no necesarios, con la misma facilidad para rechazar que para aceptar. Cada dominio gestiona su propio consentimiento (no se comparten cookies entre landing y app). Los Rastreadores de medición y marketing **solo se activan tras el consentimiento** del Usuario para la categoría correspondiente. Si el Usuario no ha aceptado marketing en el banner, el formulario de contacto muestra una casilla opcional de privacidad para que Tipi pueda enviar a Meta de qué anuncio viene y mejorar los anuncios; el Usuario puede enviar el formulario sin marcarla. Si el marketing del banner ya está aceptado, esa casilla no se muestra.

4.2. El Usuario puede modificar o retirar su consentimiento en cualquier momento a través del **panel de preferencias de privacidad**, accesible de forma permanente desde el pie de página de la web y desde los ajustes de la app (no solo en el primer acceso).

4.3. El Usuario puede además gestionar o eliminar cookies desde la configuración de su navegador. La configuración del navegador no permite un control granular por categorías.

## 5. Inhabilitación de publicidad basada en intereses

El Usuario puede obtener más información en [YourOnlineChoices (UE)](https://www.youronlinechoices.com/es/), [Network Advertising Initiative](https://optout.networkadvertising.org/) y [Digital Advertising Alliance](https://optout.aboutads.info/).

## 6. Consecuencias de rechazar rastreadores

Rechazar Rastreadores no necesarios no impide el uso del Servicio, aunque puede limitar ciertas funcionalidades de análisis o personalización.

## 7. Cambios

Podremos actualizar esta Política. La versión vigente es la publicada con la fecha indicada al inicio.

---

**Documentos relacionados**

- [Términos y Condiciones](/terms-and-conditions)
- [Política de Privacidad](/privacy-policy)
`

const dpaMarkdown = `# Contrato de Encargo de Tratamiento de Datos (DPA)

**Última actualización:** 11 de julio de 2026

Este Contrato de Encargo de Tratamiento (el «**Contrato**») forma parte del contrato de prestación del Servicio Tipi y regula el tratamiento de datos personales que **INNOVATION ROOT, S.L.** («**Tipi**» o «**Encargado**») realiza por cuenta del **Cliente** («**Responsable**») en ejecución del Servicio.

## 1. Objeto y roles

1.1. El Cliente, en su condición de **Responsable del tratamiento**, encarga a Tipi, como **Encargado del tratamiento**, el tratamiento de los datos personales necesarios para la prestación del Servicio (digitalización de albaranes/facturas, cálculo de escandallos y funcionalidades asociadas).

1.2. Tipi tratará dichos datos únicamente para prestar el Servicio y conforme a las instrucciones documentadas del Cliente, que incluyen las contenidas en el contrato principal y en este documento. Tipi **no utilizará los datos para fines propios**; en caso de hacerlo, adquiriría la condición de responsable del tratamiento con las consecuencias legales asociadas.

1.3. Tipi informará inmediatamente al Cliente si, en su opinión, una instrucción infringe el RGPD, la LOPDGDD u otra normativa de protección de datos aplicable.

## 2. Datos y afectados objeto de tratamiento

- **Categorías de datos:** datos identificativos y de contacto contenidos en los documentos que el Cliente sube (p. ej., datos de proveedores, comerciales o personas de contacto que figuren en albaranes y facturas), así como cualesquiera datos personales incorporados por el Cliente al Servicio.
- **Categorías de afectados:** proveedores del Cliente y terceros cuyos datos figuren en la documentación aportada.
- **Naturaleza y finalidad:** digitalización, extracción (OCR), almacenamiento y procesamiento para el seguimiento de costes.
- **Duración:** mientras esté vigente la relación de servicio, más los periodos de conservación aplicables.

No está previsto el tratamiento de categorías especiales de datos (art. 9 RGPD). El Cliente se abstendrá de introducir datos de dicha naturaleza.

## 3. Obligaciones de Tipi (Encargado)

Tipi se obliga a:

a) Tratar los datos solo siguiendo instrucciones documentadas del Cliente, salvo obligación legal.

b) Garantizar la confidencialidad y que las personas autorizadas se comprometan a respetarla.

c) Aplicar las medidas de seguridad del art. 32 RGPD (véase Anexo I).

d) Respetar las condiciones para recurrir a subencargados (cláusula 5).

e) Asistir al Cliente para atender los derechos de los afectados (acceso, rectificación, supresión, etc.).

f) Asistir al Cliente en el cumplimiento de sus obligaciones de seguridad, notificación de brechas y evaluaciones de impacto.

g) A elección del Cliente, suprimir o devolver los datos al finalizar la prestación (cláusula 7).

h) Poner a disposición del Cliente la información necesaria para demostrar el cumplimiento y permitir auditorías (cláusula 8).

## 4. Obligaciones del Cliente (Responsable)

El Cliente garantiza que:

a) Dispone de base jurídica válida para el tratamiento de los datos que introduce, incluidos los de terceros que figuran en su documentación.

b) Ha informado a los afectados conforme a los arts. 13-14 RGPD cuando proceda.

c) No introducirá categorías especiales de datos sin habilitación adecuada.

d) Es responsable de conservar los originales de su documentación conforme a su normativa fiscal y mercantil; **Tipi no es su archivo legal**.

## 5. Subencargados

5.1. El Cliente **autoriza** con carácter general a Tipi a recurrir a los siguientes subencargados para la prestación del Servicio:

| Subencargado | Servicio | Ubicación |
| --- | --- | --- |
| Google (Firebase) | Hosting, base de datos, autenticación | UE (\`europe-west\`) |
| MATIL SOLUTIONS, S.L. (NIF B22500458) | OCR de documentos | España (UE) |
| OpenAI | Procesamiento de voz, recetas y análisis de llamadas | EE. UU. (SCC + DPF) |
| Stripe Payments Europe, Ltd. | Procesamiento de pagos | UE/EE. UU. |

5.2. Tipi informará al Cliente de cualquier cambio previsto en la incorporación o sustitución de subencargados con una antelación mínima de **30 días naturales**, dándole la oportunidad de oponerse por motivos razonables relacionados con la protección de datos.

5.3. Tipi impondrá a los subencargados, por contrato, las mismas obligaciones de protección de datos que asume en este Contrato.

## 6. Transferencias internacionales

Cuando un subencargado trate datos fuera del EEE (p. ej., OpenAI), Tipi garantizará que existan garantías adecuadas (cláusulas contractuales tipo y/o EU-US Data Privacy Framework).

## 7. Devolución o supresión al finalizar

Finalizada la prestación, Tipi, a elección del Cliente, devolverá o suprimirá los datos personales y las copias existentes, comportando la supresión el borrado total de los datos en los sistemas y documentos de Tipi. No obstante, Tipi podrá **conservar los datos debidamente bloqueados** durante los plazos de prescripción para atender posibles responsabilidades legales, conforme al art. 33.4 LOPDGDD. Con carácter general, la supresión se producirá conforme a la política de conservación (hasta 12 meses desde la cancelación, salvo obligación legal).

## 8. Auditoría

Tipi pondrá a disposición del Cliente la información necesaria para demostrar el cumplimiento de este Contrato y permitirá auditorías, incluidas inspecciones, realizadas por el Cliente o un auditor autorizado, con preaviso razonable y sin comprometer la seguridad o confidencialidad de otros clientes. Los **costes de la auditoría correrán a cargo del Cliente**, salvo que la auditoría revele un incumplimiento grave imputable a Tipi, en cuyo caso los asumirá Tipi.

## 9. Notificación de violaciones de seguridad

Tipi notificará al Cliente sin dilación indebida y, en todo caso, dentro de las **48 horas** siguientes a tener conocimiento de una violación de la seguridad que afecte a los datos tratados por cuenta del Cliente, aportando la información disponible para que el Cliente cumpla sus obligaciones de notificación ante la autoridad de control (plazo de 72 horas del Cliente).

## 10. Responsabilidad y ley aplicable

Este Contrato se rige por la legislación española y por el RGPD y la LOPDGDD. El régimen de responsabilidad entre las partes será el previsto en el art. 82 RGPD y en el contrato principal.

### Anexo I — Medidas de seguridad (art. 32 RGPD)

- Control de acceso basado en roles y autenticación.
- Cifrado de datos en tránsito (TLS) y, cuando proceda, en reposo.
- Alojamiento en infraestructura Firebase configurada en la UE.
- Registro de accesos y trazabilidad.
- Copias de seguridad periódicas.
- Procedimiento de gestión de incidencias y brechas.
- Compromisos de confidencialidad del personal y de los subencargados.

## Aceptación

Este Contrato se entiende aceptado por el Cliente en el momento del alta del Servicio / mediante firma, con registro auditable de la aceptación (identidad del firmante, fecha/hora y versión del documento).

---

**Documentos relacionados**

- [Términos y Condiciones](/terms-and-conditions)
- [Política de Privacidad](/privacy-policy)
`

export const LEGAL_DOCUMENT_MARKDOWN: Record<LegalDocumentId, string> = {
    terms: termsMarkdown,
    privacy: privacyMarkdown,
    cookies: cookiePolicyMarkdown,
    dpa: dpaMarkdown,
}
